<script lang="ts" setup>
	import { computed, watch } from 'vue'

	import {
		TRIP_REASON_NONE,
		TRIP_REASON_OTHER,
		tripReasonCategoryIcons,
		tripReasonKey,
		tripReasons,
	} from '@/enums/tripReasons'

	import type { TripReason } from '@/modules/companies/interfaces/tripReason'

	import SectionHeader from './ui/SectionHeader.vue'

	// requests.reason es varchar(255) en BookingV2
	const OTHER_REASON_MAX_LENGTH = 255

	const legacyIcons: Record<tripReasons, string> = {
		[tripReasons.Negocio]: 'work',
		[tripReasons.Grupo]: 'groups',
		[tripReasons.Proyecto]: 'assignment',
		[tripReasons.Otro]: 'edit_note',
		[tripReasons.SinMotivo]: 'block',
	}

	interface ReasonOption {
		key: string
		label: string
		icon: string
		hint?: string | null
		badge?: string | null
	}

	/**
	 * Con catálogo (`isFallback = false`) las opciones son las de la empresa del
	 * viajero; «Otro» aparece sólo si la empresa lo permite y «Sin motivo de viaje»
	 * sólo si el motivo no es obligatorio. Sin catálogo (el endpoint falló) se
	 * ofrecen las opciones fijas de siempre y se manda el texto, como antes.
	 */
	const props = withDefaults(
		defineProps<{
			attemptedToSubmit: boolean
			options?: TripReason[]
			required?: boolean
			allowOther?: boolean
			isFallback?: boolean
			loading?: boolean
			defaultId?: number | null
		}>(),
		{
			options: () => [],
			required: false,
			allowOther: true,
			isFallback: false,
			loading: false,
			defaultId: null,
		}
	)

	const reason = defineModel<string>('reason', { required: true })
	const otherReason = defineModel<string>('otherReason', { required: true })

	const formatUntil = (value: string | null) =>
		value ?
			`Hasta ${new Date(`${value}T00:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`
		:	null

	const reasonOptions = computed<ReasonOption[]>(() => {
		if (props.isFallback) {
			return Object.values(tripReasons).map((option) => ({
				key: option,
				label: option,
				icon: legacyIcons[option],
			}))
		}

		const catalog: ReasonOption[] = props.options.map((option) => ({
			key: tripReasonKey(option.id),
			label: option.name,
			icon:
				tripReasonCategoryIcons[option.category ?? option.standardCode ?? ''] ??
				'label',
			hint: option.description,
			badge: formatUntil(option.validUntil),
		}))

		if (props.allowOther) {
			catalog.push({ key: TRIP_REASON_OTHER, label: 'Otro', icon: 'edit_note' })
		}

		if (!props.required) {
			catalog.push({
				key: TRIP_REASON_NONE,
				label: tripReasons.SinMotivo,
				icon: 'block',
			})
		}

		return catalog
	})

	const isExit = (key: string) =>
		key === TRIP_REASON_OTHER ||
		key === TRIP_REASON_NONE ||
		key === tripReasons.Otro ||
		key === tripReasons.SinMotivo

	const isOther = computed(
		() =>
			reason.value === TRIP_REASON_OTHER || reason.value === tripReasons.Otro
	)

	// Empresa sin motivos en su catálogo pero con «Otro»: no hay tarjetas que
	// mostrar, así que se va directo a «¿Cuál es tu motivo de viaje?». Mientras el
	// catálogo carga, `options` también viene vacío: sin `!loading` se elegiría
	// «Sin motivo» antes de que llegue el motivo por defecto del departamento.
	const onlyOther = computed(
		() =>
			!props.loading &&
			!props.isFallback &&
			props.options.length === 0 &&
			props.allowOther
	)

	// Sin tarjetas, la llave sale del texto: si el motivo no es obligatorio, dejarlo
	// vacío cuenta como «Sin motivo de viaje» y no bloquea la reserva. Al salir
	// (otro viajero, con catálogo) se suelta la llave que puso este watch, para que
	// el motivo por defecto vuelva a entrar.
	let autoKey: string | null = null
	watch(
		[onlyOther, otherReason, () => props.required],
		([only, text, required]) => {
			if (!only) {
				if (autoKey !== null && reason.value === autoKey) reason.value = ''
				autoKey = null
				return
			}
			autoKey = required || text.trim() ? TRIP_REASON_OTHER : TRIP_REASON_NONE
			reason.value = autoKey
		},
		{ immediate: true }
	)

	// Si la selección ya no existe (el catálogo cargó después, o la empresa lo
	// volvió obligatorio), se limpia para que la validación pida elegir de nuevo.
	watch(reasonOptions, (options) => {
		if (
			reason.value &&
			!options.some((option) => option.key === reason.value)
		) {
			reason.value = ''
		}
	})

	// El motivo por defecto del departamento del viajero viene elegido; quien
	// reserva lo puede cambiar.
	watch(
		() => [props.defaultId, reasonOptions.value] as const,
		([defaultId, options]) => {
			if (reason.value || defaultId === null) return
			const key = tripReasonKey(defaultId)
			if (options.some((option) => option.key === key)) reason.value = key
		},
		{ immediate: true }
	)

	const showReasonError = computed(
		() =>
			props.attemptedToSubmit && !reason.value && reasonOptions.value.length > 0
	)

	const showOtherReasonError = computed(
		() => props.attemptedToSubmit && isOther.value && !otherReason.value.trim()
	)

	const selectReason = (key: string) => {
		reason.value = reason.value === key ? '' : key
	}
</script>

<template>
	<div
		id="trip-reason"
		class="bg-white rounded-xl border border-gray-200 overflow-hidden"
	>
		<SectionHeader
			title="Motivo del viaje"
			subtitle="Seleccione el motivo de su viaje"
			required
			icon="luggage"
			icon-bg-color="blue"
			icon-color="blue"
		/>

		<div class="p-4 lg:p-6">
			<div
				v-if="loading"
				class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
			>
				<div
					v-for="n in 3"
					:key="n"
					class="h-14 rounded-xl bg-gray-100 animate-pulse"
				/>
			</div>

			<!--
				Obligatorio, sin «Otro» y sin un motivo vigente para el viajero: no hay nada
				que elegir y la reserva no puede salir. Se dice por qué en vez de mostrar
				una cuadrícula vacía con el error de «Selecciona un motivo».
			-->
			<div
				v-else-if="reasonOptions.length === 0"
				id="trip-reason-empty"
				role="alert"
				class="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 font-inter text-sm text-amber-900"
			>
				<span
					class="material-symbols-outlined shrink-0 text-xl text-amber-600"
					aria-hidden="true"
				>
					warning
				</span>
				<p>
					Tu empresa pide un motivo de viaje, pero no hay ninguno disponible
					para ti. Pide al administrador de tu empresa que active un motivo para
					tu departamento.
				</p>
			</div>

			<!--
				Tarjetas horizontales del mismo alto: los motivos de la empresa pueden ser
				largos («Convención Anual First Cash 2026») y en cuadros centrados se
				partían en tres renglones con alturas distintas. «Otro» y «Sin motivo» van
				con borde punteado: son salidas, no motivos del catálogo.
			-->
			<div
				v-else-if="!onlyOther"
				class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
				role="group"
				aria-label="Motivo del viaje"
				:aria-describedby="showReasonError ? 'trip-reason-error' : undefined"
			>
				<button
					v-for="option in reasonOptions"
					:key="option.key"
					type="button"
					:title="option.hint ?? option.label"
					:aria-pressed="reason === option.key"
					:class="[
						'group flex min-h-[3.5rem] items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#021F58]/40',
						isExit(option.key) ? 'border-dashed' : '',
						reason === option.key ?
							'border-[#021F58] bg-[#021F58]/5 ring-1 ring-[#021F58]'
						:	'border-gray-200 hover:border-[#021F58]/40 hover:bg-gray-50',
					]"
					@click="selectReason(option.key)"
				>
					<span
						:class="[
							'material-symbols-outlined flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xl',
							reason === option.key ?
								'bg-[#021F58] text-white'
							:	'bg-gray-100 text-gray-600 group-hover:text-[#021F58]',
						]"
						aria-hidden="true"
					>
						{{ option.icon }}
					</span>
					<span class="min-w-0 flex-1">
						<span
							:class="[
								'block font-inter text-sm font-medium leading-snug line-clamp-2',
								reason === option.key ? 'text-[#021F58]' : 'text-gray-800',
							]"
						>
							{{ option.label }}
						</span>
						<!-- La descripción va visible: `title` no se ve en pantallas táctiles. -->
						<span
							v-if="option.hint"
							class="mt-0.5 block font-inter text-xs text-gray-500 line-clamp-2"
						>
							{{ option.hint }}
						</span>
						<span
							v-if="option.badge"
							class="mt-0.5 block font-inter text-xs text-blue-700"
						>
							{{ option.badge }}
						</span>
					</span>
					<span
						v-if="reason === option.key"
						class="material-symbols-outlined shrink-0 text-lg text-[#021F58]"
						aria-hidden="true"
					>
						check_circle
					</span>
				</button>
			</div>

			<span
				id="trip-reason-error"
				v-show="showReasonError"
				class="text-xs lg:text-sm text-red-500 font-inter block mt-3"
			>
				Selecciona un motivo de viaje.
			</span>

			<div
				v-if="isOther || onlyOther"
				:class="{ 'mt-4': !onlyOther }"
			>
				<label
					for="other-trip-reason"
					class="block font-inter text-sm font-medium text-gray-700 mb-1"
				>
					¿Cuál es tu motivo de viaje?
				</label>
				<textarea
					id="other-trip-reason"
					v-model="otherReason"
					rows="3"
					:maxlength="OTHER_REASON_MAX_LENGTH"
					placeholder="Describe tu motivo de viaje"
					:aria-invalid="showOtherReasonError"
					:aria-describedby="
						showOtherReasonError ? 'other-trip-reason-error' : undefined
					"
					:class="[
						'w-full rounded-lg border px-3 py-2 font-inter text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 resize-none',
						showOtherReasonError ?
							'border-red-400 focus:border-red-500 focus:ring-red-500/20'
						:	'border-gray-300 focus:border-[#021F58] focus:ring-[#021F58]/20',
					]"
				/>
				<div class="flex justify-between gap-3 mt-1">
					<span
						id="other-trip-reason-error"
						class="text-xs lg:text-sm text-red-500 font-inter"
						:class="{ invisible: !showOtherReasonError }"
					>
						Escribe cuál es tu motivo de viaje.
					</span>
					<p class="text-xs text-gray-400 font-inter shrink-0">
						{{ otherReason.length }}/{{ OTHER_REASON_MAX_LENGTH }}
					</p>
				</div>
			</div>
		</div>
	</div>
</template>
